import { Component, inject, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef, DestroyRef, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl } from '@angular/forms';
import {
  AppealsPacketFormData,
  PacketType,
  AppealType,
  ACTION_TYPE_OPTIONS,
  ADDITIONAL_NOTES_NOMNC_OPTIONS,
  CRITERIA_USED_OPTIONS,
  ACTION_BEING_APPEALED_OPTIONS,
  CARELON_COORDINATION_ACTIONS,
  DEFAULT_TRANSITION_TEXT,
  MAX_FILE_SIZE_QIO,
  MAX_FILE_SIZE_HP,
  AuthAppealDetails,
  NoteTypesConfig,
  Note,
  ServiceLineSummary,
  PACKET_TYPES,
} from '../models/appeals-packet.model';
import { AppealsPacketService } from './appeals-packet.service';
import { NotificationService } from '@core/services';
import { IDocumentResponseDTO } from '@core/model';
import { AuthorizationPACService } from '@app/products/pac/services/authorization.service';
import { finalize, take, forkJoin } from 'rxjs';
import { DocumentSource } from '@app/core/enumerations';
import { DocumentsService } from '@core/services';
import { DocumentService } from '@core/services/document.service';


const NOTE_TYPE_PHYSICIAN_REVIEW_NOTE = 'Physician Review Note';
const NOTE_TYPE_CLINICAL_REVIEW_NOTE ='Clinical Review Note';

@Component({
  selector: 'mnc-appeals-packet',
  templateUrl: './appeals-packet.component.html',
  styleUrls: ['./appeals-packet.component.scss'],
  standalone: false
})
export class AppealsPacketComponent implements OnInit {
  @Input() authorizationByProductId!: string;
  @Input() authorizationNumber!: string;
  @Input() isExternal: boolean = false;
  @Input() lineOfBusiness?: string;
  @Input() tenantName?: string;
  @Input() tenantId!: string;
  @Output() appealPacketGenerated = new EventEmitter<void>();

  documentFilter = (doc: IDocumentResponseDTO) => this.appealsPacketService.filterDocuments([doc]).length > 0;
  private readonly fb = inject(FormBuilder);
  private readonly appealsPacketService = inject(AppealsPacketService);
  private readonly notificationService = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authorizationService = inject(AuthorizationPACService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentsService = inject(DocumentsService);
  private readonly documentService = inject(DocumentService);

  private noteTypesConfigList: NoteTypesConfig[] = [];
  private latestPhysicianComment: string = '';
  serviceLineNotes: Note[] = [];
  serviceLinesSummary: ServiceLineSummary[] = [];
  showSupportingFact = false;

  selectQuery = [
    'appealAndGrievanceId',
    'authorizationNumber',
    'referenceNumber',
    'receivedOn',
    'activityType',
    'urgency',
    'issueDescription',
    'status',
    'dueDateTime'
  ];

  appealsForm!: FormGroup;
  showAuthAppealDetails = false;
  authAppealDetails: AuthAppealDetails | null = null;
  isGenerating = false;

  // Dropdown options
  appealTypeOptions: AppealType[] = ['Expedited', 'Standard'];
  exhibitOptions = [];
  actionTypeOptions = ACTION_TYPE_OPTIONS;
  additionalNotesNomncOptions = ADDITIONAL_NOTES_NOMNC_OPTIONS;
  primaryDenialReasons = [];
  criteriaUsedOptions = CRITERIA_USED_OPTIONS;
  actionBeingAppealedOptions = ACTION_BEING_APPEALED_OPTIONS;
  carelonCoordinationActionsOptions = CARELON_COORDINATION_ACTIONS;

  // Computed properties
  get packetTypeOptions(): PacketType[] {
    return Object.values(PACKET_TYPES);
  }

  get selectedPacketType(): PacketType | null {
    return this.appealsForm?.get('packetType')?.value;
  }

  get isQIOAppeal(): boolean {
    return this.selectedPacketType === PACKET_TYPES.QIOAppeal;
  }

  get isHPAppeal(): boolean {
    return this.selectedPacketType ===PACKET_TYPES.HPAppeal;
  }

  get maxFileSizeLabel(): string {
    if (this.isQIOAppeal) {
      return `Max File Size Limit: ${MAX_FILE_SIZE_QIO}MB`;
    } else if (this.isHPAppeal) {
      return `Max File Size Limit: ${MAX_FILE_SIZE_HP}MB`;
    }
    return '';
  }

  get isFormValid(): boolean {
    return this.appealsForm?.valid || false;
  }

  get caseSnapshotFormatMessage(): string {
    if (!this.authAppealDetails) {
      return '';
    }

    const memberAge = this.calculateAge(this.authAppealDetails.dateOfBirth);
    const setting = this.authAppealDetails.facilityType?.description || '';
    const admissionDate = this.formatDateOnly(this.authAppealDetails.admitDate);

    return `${memberAge} year old member admitted to ${setting} on ${admissionDate} for`;
  }

  get caseSnapshotContinuationMessage(): string {
    if (!this.authAppealDetails) {
      return '';
    }

    const setting = this.authAppealDetails.facilityType?.description || '';
    const ladValue = this.appealsForm?.get('lastApprovedDay')?.value;
    const lad = ladValue ? this.formatDateOnly(ladValue) : '—';

    return `Current request is for continued ${setting} stay beyond LAD of ${lad}`;
  }

  get formattedAdmissionDate(): string {
    if (!this.authAppealDetails) {
      return '';
    }
    return this.formatDateOnly(this.authAppealDetails.admitDate);
  }

  private formatDateOnly(dateString: string | undefined): string {
    if (!dateString) return '<Admission Date>';
    
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  }

  private mapPacketTypeToEnum(value: string): string {
    const mapping: { [key: string]: string } = {
      'QIO Appeal': 'QIOAppeal',
      'HP Appeal': 'HPAppeal'
    };
    return mapping[value] || value;
  }

  private mapActionTypeToEnum(value: string): string {
    const mapping: { [key: string]: string } = {
      'End all covered services (NOMNC/DENC)': 'EndAllCoveredServices',
      'Reduce covered services': 'ReduceCoveredServices',
      'Deny admission to Post-Acute care': 'DenyAdmissionToPostAcuteCare',
      'Other': 'Other'
    };
    return mapping[value] || value;
  }

  private calculateAge(dateOfBirth: string | undefined): string {
    if (!dateOfBirth) return '<Member Age>';
    
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age.toString();
  }

  get carelonCoordinationActionsArray(): FormArray {
    return this.appealsForm.get('carelonCoordinationActions') as FormArray;
  }

  get outreachRfiDatesArray(): FormArray {
    return this.appealsForm.get('outreachRfiDates') as FormArray;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormListeners();
    this.loadReferences();
    this.loadPhysicianNotesForRationale();
  }

  private loadReferences(): void {    
    this.appealsPacketService.getAppealsPacketReferences().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (refs) => {
        if (refs.exhibits && refs.exhibits.length > 0) {
          this.exhibitOptions = refs.exhibits;
        }
        
        if (refs.denialReasons && refs.denialReasons.length > 0) {
          this.primaryDenialReasons = refs.denialReasons;
        }
      },
      error: (error) => {
        console.error('Error loading references from API:', error);
      }
    });
  }

  loadAppealsAndGrievancesByAuthNumber(authorizationNumber: string): void {
    const selectFields = this.selectQuery.join(',');
    const queryParams = `?$filter=authorizationNumber eq '${authorizationNumber}'&$select=${selectFields}`;
    this.appealsPacketService.getAppealsAndGrievances(this.tenantId, queryParams, this.isExternal)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result && result.items && result.items.length > 0) {
            let filteredItems = result.items;
            
            if (this.selectedPacketType === PACKET_TYPES.QIOAppeal) {
              filteredItems = result.items.filter((item: any) => item.activityType === 'QIO');
              if (filteredItems.length > 0) {
              // Sort by receivedOn date in descending order to get the latest one
              filteredItems.sort((a: any, b: any) => {
                const dateA = new Date(a.receivedOn).getTime();
                const dateB = new Date(b.receivedOn).getTime();
                return dateB - dateA;
              });
              
              const appealAndGrievanceId = filteredItems[0].appealAndGrievanceId;
              
              forkJoin({
                agData: this.appealsPacketService.getAppealsAndGrievancesData(appealAndGrievanceId, this.isExternal),
                notificationData: this.appealsPacketService.getAppealAndGrievanceNotification(appealAndGrievanceId, this.isExternal)
              }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: ({ agData, notificationData }) => {
                  if (this.authAppealDetails) {
                    this.authAppealDetails.appealAndGrievanceId = appealAndGrievanceId;
                    this.authAppealDetails.agReferenceNumber = agData?.referenceNumber;
                    this.authAppealDetails.qioNotificationReceivedDate = agData?.receivedOn;
                    this.authAppealDetails.dencDeliveredDate = notificationData?.detailedExplanationOfNonCoverageDTO?.mailedToBeneficiaryDate;
                    this.authAppealDetails.nomncDeliveredToServiceProviderDate = notificationData?.noticeOfMedicareNonCoverageDTO?.deliveredServiceProviderDate;
                    this.authAppealDetails.caseAppealNumber = agData?.healthPlanReferenceNumber;
                    this.authAppealDetails.nomncDeliverySignatureDate = notificationData?.noticeOfMedicareNonCoverageDTO?.deliverySignatureDate;
                    this.authAppealDetails.appealFiledDate = agData?.appealFiledDate;
                  }
                },
                error: (error) => {
                  console.error('Error loading appeals and grievances data:', error);
                }
              });
            }
            } 
            else if (this.selectedPacketType === PACKET_TYPES.HPAppeal) {
              filteredItems = result.items.filter((item: any) => item.activityType === 'Appeal');
              if (filteredItems.length > 0) {
                const appealDataPromises = filteredItems.map(element => 
                  this.appealsPacketService.getAppealsAndGrievancesData(element.appealAndGrievanceId, this.isExternal).toPromise()
                );
                
                Promise.all(appealDataPromises).then((allData) => {
                  const healthPlanAppeals = allData.filter((data: any) => data?.appealSource === 'Health Plan');
                  
                  if (healthPlanAppeals.length > 0) {
                    healthPlanAppeals.sort((a: any, b: any) => {
                      const dateA = new Date(a.receivedOn).getTime();
                      const dateB = new Date(b.receivedOn).getTime();
                      return dateB - dateA;
                    });
                    
                    const latestAppeal = healthPlanAppeals[0];
                    if (this.authAppealDetails && latestAppeal.referenceNumber) {
                      this.authAppealDetails.appealAndGrievanceId = latestAppeal.appealAndGrievanceId;
                      this.authAppealDetails.agReferenceNumber = latestAppeal.referenceNumber;
                      this.authAppealDetails.caseAppealNumber = latestAppeal.healthPlanReferenceNumber;
                      this.authAppealDetails.appealFiledDate = latestAppeal.appealFiledDate;
                    }
                  }
                }).catch((error) => {
                  console.error('Error loading appeals and grievances data:', error);
                });
              }
            }
          }
        },
        error: (error) => {
          console.error('Error loading appeals and grievances:', error);
        }
      });
  }

  private initializeForm(): void {
    this.appealsForm = this.fb.group({
      // Common fields
      packetType: [null, Validators.required],
      lastApprovedDay: [null, Validators.required],
      actionTypes: [[], Validators.required],
      actionTypeOther: [''],
      appealType: ['Expedited', Validators.required],
      primaryHealthPlanContact: ['', Validators.maxLength(5000)],

      // QIO specific fields
      signedNomnc: [null],
      additionalNotesOnNomnc: [''],
      additionalNotesOnNomncOther: [''],
      carelonFollowUpDocumented: [null],
      carelonFollowUpDate: [null],

      // Exhibits fields
      exhibits: [[], Validators.required],
      caseManagementNotesDetails: [''],

      // UM Determination Summary fields
      actionBeingAppealed: [[], Validators.required],
      actionBeingAppealedOther: [''],
      caseSnapshot: ['', Validators.required],
      historyOfPresentIllness: [''],
      primaryDenialReasons: [[], Validators.required],
      supportingFacts: this.fb.array([]),
      criteriaUsed: [[], Validators.required],
      criteriaUsedOther: [''],
      primaryRationale: ['', Validators.required],
      primaryDocumentReviewed: [null, Validators.required],
      primaryDocumentsReviewedText: ['', Validators.required],

      // Transition of Care Coordination fields
      carelonCoordinationActions: this.fb.array([], Validators.required),
      carelonCoordinationNotes: [''],
      carelonCoordinationOtherDetails: [''],

      // Chronology of Events fields
      outreachRfiDates: this.fb.array([]),

      // Denial to Evidence Crosswalk fields
      transitionOfCareCoordination: [DEFAULT_TRANSITION_TEXT, Validators.required],

      // Selected documents
      selectedDocumentIds: [[]],
      selectedDocuments: [[]],
      documentFileName: ['']
    });
  }

  private setupFormListeners(): void {
    // Listen to packet type changes
    this.appealsForm.get('packetType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((packetType: PacketType | null) => {
        this.onPacketTypeChange(packetType);
      });

    // Listen to signed NOMNC changes
    this.appealsForm.get('signedNomnc')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        this.onSignedNomncChange(value);
      });

    // Listen to carelon follow-up documented changes
    this.appealsForm.get('carelonFollowUpDocumented')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean | null) => {
        this.onCarelonFollowUpChange(value);
      });

    // Listen to exhibits changes
    this.appealsForm.get('exhibits')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((exhibits: string[]) => {
        this.onExhibitsChange(exhibits);
      });

    // Listen to primary document reviewed changes
    this.appealsForm.get('primaryDocumentReviewed')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        this.onPrimaryDocumentReviewedChange(value);
      });

    // Listen to primary denial reasons changes
    this.appealsForm.get('primaryDenialReasons')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reasons: string[]) => {
        this.onPrimaryDenialReasonsChange(reasons);
      });

    // Listen to additional notes on NOMNC changes
    this.appealsForm.get('additionalNotesOnNomnc')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        this.onAdditionalNotesNomncChange(value);
      });

    // Listen to action types changes
    this.appealsForm.get('actionTypes')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((actionTypes: string[]) => {
        this.onActionTypesChange(actionTypes);
      });

    // Listen to action being appealed changes
    this.appealsForm.get('actionBeingAppealed')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((actionBeingAppealed: string[]) => {
        this.onActionBeingAppealedChange(actionBeingAppealed);
      });

    // Listen to criteria used changes
    this.appealsForm.get('criteriaUsed')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((criteriaUsed: string[]) => {
        this.onCriteriaUsedChange(criteriaUsed);
      });

    // Listen to carelon coordination actions changes
    this.appealsForm.get('carelonCoordinationActions')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onCarelonCoordinationActionsChange();
      });
  }

  private onPacketTypeChange(packetType: PacketType | null): void {
    if (!packetType) {
      return;
    }

    this.appealsForm.patchValue({
      lastApprovedDay: null,
      actionTypes: [],
      actionTypeOther: '',
      appealType: 'Expedited',
      primaryHealthPlanContact: '',

      signedNomnc: null,
      additionalNotesOnNomnc: '',
      additionalNotesOnNomncOther: '',
      carelonFollowUpDocumented: null,
      carelonFollowUpDate: null,

      exhibits: [],
      caseManagementNotesDetails: '',

      actionBeingAppealed: [],
      actionBeingAppealedOther: '',
      caseSnapshot: '',
      historyOfPresentIllness: '',
      primaryDenialReasons: [],
      criteriaUsed: [],
      criteriaUsedOther: '',
      primaryRationale: '',
      primaryDocumentReviewed: null,
      primaryDocumentsReviewedText: '',

      carelonCoordinationNotes: '',
      carelonCoordinationOtherDetails: '',

      transitionOfCareCoordination: DEFAULT_TRANSITION_TEXT,

      selectedDocumentIds: [],
      selectedDocuments: [],
      documentFileName: ''
    });

    // Clear FormArrays
    this.supportingFactsArray.clear();
    this.carelonCoordinationActionsArray.clear();
    this.outreachRfiDatesArray.clear();

    this.showSupportingFact = false;
    this.authAppealDetails = null;

    // Update validators based on packet type
    if (packetType === PACKET_TYPES.QIOAppeal) {
      this.appealsForm.get('signedNomnc')?.setValidators(Validators.required);
      this.appealsForm.get('carelonFollowUpDocumented')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('signedNomnc')?.clearValidators();
      this.appealsForm.get('carelonFollowUpDocumented')?.clearValidators();
    }

    this.appealsForm.get('signedNomnc')?.updateValueAndValidity();
    this.appealsForm.get('carelonFollowUpDocumented')?.updateValueAndValidity();

    // Load auth/appeal details
    this.loadAuthAppealDetails(packetType);
    this.loadPhysicianNotesForRationale();
  }

  private onSignedNomncChange(value: string | null): void {
    if (value === 'No') {
      this.appealsForm.get('additionalNotesOnNomnc')?.setValidators(Validators.required);
      this.appealsForm.get('carelonFollowUpDocumented')?.setValue(null);
      this.appealsForm.get('carelonFollowUpDate')?.setValue(null);
    } else {
      this.appealsForm.get('additionalNotesOnNomnc')?.clearValidators();
      this.appealsForm.get('additionalNotesOnNomnc')?.setValue('');
      this.appealsForm.get('additionalNotesOnNomncOther')?.clearValidators();
      this.appealsForm.get('additionalNotesOnNomncOther')?.setValue('');
      this.appealsForm.get('carelonFollowUpDocumented')?.clearValidators();
      this.appealsForm.get('carelonFollowUpDocumented')?.setValue(null);
      this.appealsForm.get('carelonFollowUpDate')?.clearValidators();
      this.appealsForm.get('carelonFollowUpDate')?.setValue(null);
    }
    this.appealsForm.get('additionalNotesOnNomnc')?.updateValueAndValidity();
    this.appealsForm.get('additionalNotesOnNomncOther')?.updateValueAndValidity();
    this.appealsForm.get('carelonFollowUpDocumented')?.updateValueAndValidity();
    this.appealsForm.get('carelonFollowUpDate')?.updateValueAndValidity();
  }

  private onCarelonFollowUpChange(value: boolean | null): void {
    if (value === true) {
      this.appealsForm.get('carelonFollowUpDate')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('carelonFollowUpDate')?.clearValidators();
      this.appealsForm.get('carelonFollowUpDate')?.setValue(null);
    }
    this.appealsForm.get('carelonFollowUpDate')?.updateValueAndValidity();
  }

  private onExhibitsChange(exhibits: string[]): void {
    if (!exhibits) {
      return;
    }
    
    if (exhibits.includes('Case management notes/referrals')) {
      this.appealsForm.get('caseManagementNotesDetails')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('caseManagementNotesDetails')?.clearValidators();
      this.appealsForm.get('caseManagementNotesDetails')?.setValue('');
    }
    this.appealsForm.get('caseManagementNotesDetails')?.updateValueAndValidity();
  }

  private onPrimaryDocumentReviewedChange(value: string | null): void {
    this.appealsForm.get('primaryDocumentsReviewedText')?.setValue('');
    if (value === 'Clinical Review Note') {
      this.loadClinicalReviewNoteComments();
    } else if (value === 'Physician Review Note') {
      this.loadPhysicianReviewNoteComments();
    } else {
      this.appealsForm.get('primaryDocumentsReviewedText')?.setValue('');
    }
  }

  private onAdditionalNotesNomncChange(value: string): void {
    if (value === 'Other') {
      this.appealsForm.get('additionalNotesOnNomncOther')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('additionalNotesOnNomncOther')?.clearValidators();
      this.appealsForm.get('additionalNotesOnNomncOther')?.setValue('');
    }
    this.appealsForm.get('additionalNotesOnNomncOther')?.updateValueAndValidity();
  }

  private onActionTypesChange(actionTypes: string[]): void {
    if (actionTypes && actionTypes.includes('Other')) {
      this.appealsForm.get('actionTypeOther')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('actionTypeOther')?.clearValidators();
      this.appealsForm.get('actionTypeOther')?.setValue('');
    }
    this.appealsForm.get('actionTypeOther')?.updateValueAndValidity();
  }

  private onActionBeingAppealedChange(actionBeingAppealed: string[]): void {
    if (actionBeingAppealed && actionBeingAppealed.includes('Other')) {
      this.appealsForm.get('actionBeingAppealedOther')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('actionBeingAppealedOther')?.clearValidators();
      this.appealsForm.get('actionBeingAppealedOther')?.setValue('');
    }
    this.appealsForm.get('actionBeingAppealedOther')?.updateValueAndValidity();
  }

  private onCriteriaUsedChange(criteriaUsed: string[]): void {
    if (criteriaUsed && criteriaUsed.includes('Other')) {
      this.appealsForm.get('criteriaUsedOther')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('criteriaUsedOther')?.clearValidators();
      this.appealsForm.get('criteriaUsedOther')?.setValue('');
    }
    this.appealsForm.get('criteriaUsedOther')?.updateValueAndValidity();
  }

  private onCarelonCoordinationActionsChange(): void {
    const hasOther = this.carelonCoordinationActionsArray.controls.some(
      control => control.get('action')?.value === 'Other'
    );
    
    if (hasOther) {
      this.appealsForm.get('carelonCoordinationOtherDetails')?.setValidators(Validators.required);
    } else {
      this.appealsForm.get('carelonCoordinationOtherDetails')?.clearValidators();
      this.appealsForm.get('carelonCoordinationOtherDetails')?.setValue('');
    }
    this.appealsForm.get('carelonCoordinationOtherDetails')?.updateValueAndValidity();
  }

  private loadAuthAppealDetails(packetType: PacketType): void {
    this.appealsPacketService.getAuthAppealDetails(this.authorizationNumber, packetType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => {
          this.authAppealDetails = details;
          this.getServicingFacilityData();
          this.loadMedicalHistory();
          this.loadAppealsAndGrievancesByAuthNumber(this.authorizationNumber);
        },
        error: (error) => {
          console.error('Error loading auth/appeal details:', error);
          this.notificationService.showError('Failed to load authorization details');
        }
      });
  }

  private loadMedicalHistory(): void {
    this.appealsPacketService.getMedicalHistory(this.authorizationByProductId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (historyOfPresentIllness) => {
          if (historyOfPresentIllness) {
            // Populate only the history text
            this.appealsForm.get('caseSnapshot')?.setValue(historyOfPresentIllness);
          }
        },
        error: (error) => {
          console.error('Error loading medical history:', error);
        }
      });
  }

  private loadPhysicianNotesForRationale(): void {
    this.loadServiceLineNotes(NOTE_TYPE_PHYSICIAN_REVIEW_NOTE,(physicianNotes) => {
      if (physicianNotes && physicianNotes.length > 0) {
        // Sort by date to get the latest note
        const sortedNotes = [...physicianNotes].sort((a: any, b: any) => {
          const dateA = Math.max(new Date(a.modifiedOn || 0).getTime(), new Date(a.createdOn || a.noteDate || 0).getTime());
          const dateB = Math.max(new Date(b.modifiedOn || 0).getTime(), new Date(b.createdOn || b.noteDate || 0).getTime());
          return dateB - dateA;
        });
        
        const latestNote = sortedNotes[0];
        const comments = latestNote?.physicianComment || '';
        
        this.latestPhysicianComment = comments;
        this.appealsForm.get('primaryRationale')?.setValue(comments);
      }
    });
  }

  private loadPhysicianReviewNoteComments(): void {
    this.loadServiceLineNotes(NOTE_TYPE_PHYSICIAN_REVIEW_NOTE,(physicianNotes) => {

      if (physicianNotes && physicianNotes.length > 0) {
        // Sort by date to get the latest note
        const sortedNotes = [...physicianNotes].sort((a: any, b: any) => {
          const dateA = Math.max(new Date(a.modifiedOn || 0).getTime(), new Date(a.createdOn || a.noteDate || 0).getTime());
          const dateB = Math.max(new Date(b.modifiedOn || 0).getTime(), new Date(b.createdOn || b.noteDate || 0).getTime());
          return dateB - dateA;
        });
        
        const latestNote = sortedNotes[0];
        const comments = latestNote?.physicianComment || '';
        // Only populate primaryDocumentsReviewedText when user selects Physician Review Note
        this.appealsForm.get('primaryDocumentsReviewedText')?.setValue(comments);
      }
    });
  }

  private loadClinicalReviewNoteComments(): void {
    
    this.loadServiceLineNotes(NOTE_TYPE_CLINICAL_REVIEW_NOTE,(clincalNotes) => {
      if (clincalNotes && clincalNotes.length > 0) {
        // Sort by date to get the latest note
        const sortedNotes = [...clincalNotes].sort((a: any, b: any) => {
          const dateA = Math.max(new Date(a.modifiedOn || 0).getTime(), new Date(a.createdOn || a.noteDate || 0).getTime());
          const dateB = Math.max(new Date(b.modifiedOn || 0).getTime(), new Date(b.createdOn || b.noteDate || 0).getTime());
          return dateB - dateA;
        });
        
        const latestNote = sortedNotes[0];
        const comments = decodeURI(latestNote?.comment || '');
        this.appealsForm.get('primaryDocumentsReviewedText')?.setValue(comments);
      }
    });
  }

  showAuthAppealDetailsModal(): void {
    if (!this.selectedPacketType) {
      return;
    }
    this.showAuthAppealDetails = true;
    this.loadAppealsAndGrievancesByAuthNumber(this.authorizationNumber);
  }

  private onPrimaryDenialReasonsChange(reasons: string[]): void {
    const supportingFactsArray = this.appealsForm.get('supportingFacts') as FormArray;
    
    if (reasons && reasons.length > 0) {
      this.showSupportingFact = true;
      supportingFactsArray.clear();
      
      reasons.forEach(reason => {
        const factGroup = this.fb.group({
          denialReason: [reason],
          supportingFact: [this.latestPhysicianComment, Validators.required]
        });
        
        supportingFactsArray.push(factGroup);
      });
    } else {
      this.showSupportingFact = false;
      supportingFactsArray.clear();
    }
  }

  private loadServiceLineNotes(noteType: string, callback?: (notes: any[]) => void): void {
    this.authorizationService.getServiceLinesSummarybyAuthNumber(this.tenantId, this.authorizationNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (serviceLines) => {
          this.serviceLinesSummary = serviceLines || [];
          
          if (this.serviceLinesSummary.length === 0) {
            return;
          }

          // Sort by createdDate descending to get the latest service line
          const sortedServiceLines = [...this.serviceLinesSummary].sort((a: any, b: any) => {
            const dateA = new Date(a.createdDate || a.createdOn).getTime();
            const dateB = new Date(b.createdDate || b.createdOn).getTime();
            return dateB - dateA; // Descending order (latest first)
          });

          // Get the latest service line
          const latestServiceLine = sortedServiceLines[0];

          // Build note types config for the latest service line only
          this.noteTypesConfigList = [{
            noteTypes: [noteType],
            referenceId: latestServiceLine.serviceLineId
          }];

          // Fetch physician notes for the latest service line
          this.appealsPacketService.getAuthNotesByTypes(this.authorizationNumber, this.noteTypesConfigList)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (serviceLineNotes) => {
                if (Array.isArray(serviceLineNotes)) {
                  this.serviceLineNotes = serviceLineNotes;
                  
                  // Call callback if provided
                  if (callback) {
                    callback(this.serviceLineNotes);
                  }
                }
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error('Error loading physician notes:', error);
              }
            });
        },
        error: (error) => {
          console.error('Error loading service lines summary:', error);
        }
      });
  }

  closeAuthAppealDetailsModal(): void {
    this.showAuthAppealDetails = false;
  }

  addCarelonCoordinationAction(action: string): void {
    const actionGroup = this.fb.group({
      action: [action],
      actionDate: [null, action !== 'Other' && action !== 'No additional discharge planning detail was provided before final determination' ? Validators.required : [] ]
    });
    this.carelonCoordinationActionsArray.push(actionGroup);
  }

  removeCarelonCoordinationAction(index: number): void {
    this.carelonCoordinationActionsArray.removeAt(index);
  }

  onCarelonCoordinationActionChange(event: any, action: string): void {
    if (event.checked) {
      this.addCarelonCoordinationAction(action);
    } else {
      const index = this.carelonCoordinationActionsArray.controls.findIndex(
        control => control.get('action')?.value === action
      );
      if (index !== -1) {
        this.removeCarelonCoordinationAction(index);
      }
    }
  }

  get supportingFactsArray(): FormArray {
    return this.appealsForm.get('supportingFacts') as FormArray;
  }

  isCarelonCoordinationActionSelected(action: string): boolean {
    return this.carelonCoordinationActionsArray.controls.some(
      control => control.get('action')?.value === action
    );
  }

  addOutreachRfiDate(): void {
    this.outreachRfiDatesArray.push(this.fb.control(null, Validators.required));
  }

  removeOutreachRfiDate(index: number): void {
    this.outreachRfiDatesArray.removeAt(index);
  }

  onDocumentSelectionChange(selectedDocs: IDocumentResponseDTO[]): void {
    if (selectedDocs && selectedDocs.length > 0) {

      const selectedIds = selectedDocs.map(doc => doc.docMetaDataId);
      const allOriginalDocuments = this.documentService.getOriginalDocumentsByIds(selectedIds);
      
      const selectedDocumentsInfo = allOriginalDocuments.map(doc => ({
        docMetaDataId: doc.docMetaDataId,
        documentSource: doc.documentSource,
        documentFileName: doc.documentFileName
      }));
      
      // Extract all the document IDs from the expanded list
      const allDocumentIds = allOriginalDocuments.map(doc => doc.docMetaDataId);
      
      this.appealsForm.get('selectedDocumentIds')?.setValue(allDocumentIds);
      this.appealsForm.get('selectedDocuments')?.setValue(selectedDocumentsInfo);
      
      const lastDocument = selectedDocs[selectedDocs.length - 1];
      this.appealsForm.get('documentFileName')?.setValue(lastDocument?.documentFileName || '');
    } else {
      this.appealsForm.get('selectedDocumentIds')?.setValue([]);
      this.appealsForm.get('selectedDocuments')?.setValue([]);
      this.appealsForm.get('documentFileName')?.setValue('');
    }
  }

  generateAppealsPacket(): void {

    this.isGenerating = true;
    const formData: AppealsPacketFormData = this.appealsForm.value;

    if (formData.exhibits && formData.exhibits.length > 0) {
      formData.exhibits = this.exhibitOptions.filter(option => formData.exhibits.includes(option));
    }
    if (formData.actionTypes && formData.actionTypes.length > 0) {
      formData.actionTypes = this.actionTypeOptions.filter(option => formData.actionTypes.includes(option as any)) as any;
    }
    if (formData.actionBeingAppealed && formData.actionBeingAppealed.length > 0) {
      formData.actionBeingAppealed = this.actionBeingAppealedOptions.filter(option => formData.actionBeingAppealed.includes(option));
    }
    if (formData.primaryDenialReasons && formData.primaryDenialReasons.length > 0) {
      formData.primaryDenialReasons = this.primaryDenialReasons.filter(option => formData.primaryDenialReasons.includes(option));
    }
    if (formData.criteriaUsed && formData.criteriaUsed.length > 0) {
      formData.criteriaUsed = this.criteriaUsedOptions.filter(option => formData.criteriaUsed.includes(option));
    }
    if (formData.carelonCoordinationActions && formData.carelonCoordinationActions.length > 0) {
      const order = this.carelonCoordinationActionsOptions;
      formData.carelonCoordinationActions = formData.carelonCoordinationActions
        .slice()
        .sort((a, b) => order.indexOf(a.action) - order.indexOf(b.action));
    }


    // Create combined payload with form data and auth/appeal details
    const payload = {
      ...formData,
      packetType: this.mapPacketTypeToEnum(formData.packetType),
      actionTypes: formData.actionTypes?.map(at => this.mapActionTypeToEnum(at)) || [],
      authAppealDetails: this.authAppealDetails,
      documentOrigin: 'Manual',
      tenantId: this.tenantId,
      tenantName: this.tenantName,
      memberAge: this.authAppealDetails?.dateOfBirth ? this.calculateAge(this.authAppealDetails.dateOfBirth) : null,
      documentSource: DocumentSource.Appeals
    };

    this.notificationService.showInfo('The packet may take a few minutes before being available to download.', 'Appeal Packet Sent to Queue:');
    this.appealsPacketService.generateAppealsPacket(payload, this.authorizationByProductId)
      .pipe(finalize(() => this.isGenerating = false))
      .subscribe({
        next: (response: string) => {
          if (response) {
            this.downloadAppealPacketDocument(response);
            this.notificationService.showSuccess('The appeal packet will be downloaded.', 'Appeal Packet Generated:');
            this.appealPacketGenerated.emit();
          } else {
            this.notificationService.showStickyError('There was an error generating the appeal packet. Please try again later.', 'Unable to Generate Appeal Packet:');
          }
          this.resetForm();
        },
        error: (error) => {          
          if (error.status === 400 && error.error?.includes('appeal packet exceeds')) {
            this.notificationService.showStickyError(error.error, 'Unable to Generate Appeal Packet:');
          } else {
            this.notificationService.showStickyError('There was an error generating the appeal packet. Please try again later.', 'Unable to Generate Appeal Packet:');
          }
        }
      });
  }

  downloadAppealPacketDocument(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.click();
  }

  private getServicingFacilityData(): void {
    this.appealsPacketService.getByAuthorization(this.tenantId, this.authorizationNumber).pipe(
      take(1),
      finalize(() => {
        this.cdr.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (facility) => {
        if (this.authAppealDetails) {
          this.authAppealDetails.facilityName = facility.providerAccountDTO.providerName;
        }
      },
      error: (error) => {
        console.error('Error loading facility data:', error);
      }
    });
  }

  private resetForm(): void {
    this.appealsForm.reset();
    this.appealsForm.patchValue({
      appealType: 'Expedited',
      transitionOfCareCoordination: DEFAULT_TRANSITION_TEXT
    });
    
    // Clear form arrays
    this.carelonCoordinationActionsArray.clear();
    this.outreachRfiDatesArray.clear();
    
    // Reset auth appeal details
    this.authAppealDetails = null;
  }
}
